import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Search, Sparkles, ScrollText, Swords, Map, Trophy, Star, Share2, CheckCircle2, Flame, Ticket, Compass, Landmark, Play } from "lucide-react";

const CAT_ICON = { skilling: Star, quests: ScrollText, diaries: Map, combat: Swords, raids: Landmark, collection: Trophy, clues: Compass };
const GAMEMODES = [["main", "Main"], ["ironman", "Ironman"], ["hardcore", "HC Ironman"], ["ultimate", "UIM"], ["group", "Group IM"], ["hardcore_group", "HC Group IM"]];

export const TryDemo = ({ onRegister }) => {
  const navigate = useNavigate();
  const [rsn, setRsn] = useState("");
  const [gm, setGm] = useState("main");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ today: 0, week: 0, all_time: 0 });

  useEffect(() => {
    api.get("/public/demo-stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    if (!rsn.trim()) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/public/demo/${encodeURIComponent(rsn.trim())}?gamemode=${gm}`);
      setResult(data);
      setStats((s) => ({ today: s.today + 1, week: s.week + 1, all_time: s.all_time + 1 }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't fetch that account.");
    } finally { setBusy(false); }
  };

  return (
    <div className="osrs-panel p-5 min-w-0 w-full" data-testid="try-demo">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={18} className="text-[#FFCF3F]" />
        <h2 className="osrs-title text-lg">Preview a few tasks — no signup</h2>
      </div>
      <p className="osrs-shadow text-[#b9a06a] text-sm mb-3">
        Enter your RuneScape name for a quick <span className="text-[#E6A519]">sample</span> of tasks from your live stats. The full tracker adds hundreds more across skilling, quests, diaries, combat &amp; collection log — plus auto-tracking, streaks, weekly recaps &amp; leaderboards.
      </p>
      {stats.all_time >= 100 && (
        <p data-testid="demo-tried-count" className="osrs-num text-[#7CFC7C] text-xs mb-3">
          ⚔️ {stats.all_time.toLocaleString()} adventurers got their tasks{stats.week >= 20 ? ` · ${stats.week.toLocaleString()} this week` : ""}
        </p>
      )}
      <form onSubmit={run} className="mb-2">
        <div className="flex gap-2 mb-2">
          <input data-testid="demo-rsn" value={rsn} onChange={(e) => setRsn(e.target.value)}
            placeholder="e.g. Zezima" className="osrs-input flex-1 min-w-0 px-3 py-2" />
          <select data-testid="demo-gamemode" value={gm} onChange={(e) => setGm(e.target.value)}
            className="osrs-input px-2 py-2 shrink-0">
            {GAMEMODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <button data-testid="demo-go" type="submit" disabled={busy}
          className="osrs-btn osrs-btn-green w-full py-2 flex items-center justify-center gap-1">
          <Search size={15} />{busy ? "Fetching your stats..." : "Get my tasks"}
        </button>
      </form>
      {error && <p data-testid="demo-error" className="text-[#FF6666] osrs-shadow text-sm">{error}</p>}
      {result && (
        <div data-testid="demo-result" className="mt-3">
          <p className="osrs-shadow text-[#E6A519] text-sm mb-2">
            <span className="text-[#FFCF3F] osrs-num">{result.rsn}</span> · CB {result.combat_level} · Total {result.total_level}
          </p>
          {(() => {
            const pct = Math.min(100, Math.max(1, Math.round((result.total_level / 2277) * 100)));
            return (
              <div data-testid="demo-completion" className="mb-3 bg-[#241b10] border-2 border-black p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="osrs-shadow text-[#b9a06a] text-xs">Account completion</span>
                  <span className="osrs-num text-[#FFCF3F] text-xs">{pct}% to max total</span>
                </div>
                <div className="h-2.5 bg-black border border-[#4b3a1e] overflow-hidden">
                  <div className="h-full bg-[#4caf3f]" style={{ width: `${pct}%`, transition: "width 0.6s ease" }} />
                </div>
                <p className="osrs-shadow text-[#7CFC7C] text-[11px] mt-1">Sign up to track this % climb toward 2277 total level.</p>
              </div>
            );
          })()}
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {result.tasks.map((t, i) => {
              const Icon = CAT_ICON[t.category] || Star;
              return (
                <div key={i} data-testid="demo-task" className="flex items-start gap-2 bg-[#241b10] border-2 border-black p-2">
                  <Icon size={16} className="text-[#7CFC7C] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="osrs-num text-[#FFCF3F] text-sm">
                      {t.title} <span className="text-[#7CFC7C] text-xs">+{t.points}</span>
                      {t.tier && <span className="text-[#b9a06a] text-[10px] ml-1 uppercase">· {t.tier}</span>}
                    </p>
                    <p className="osrs-shadow text-[#b9a06a] text-xs">{t.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div data-testid="demo-payoff" className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1 text-[#E6A519] osrs-shadow text-[11px]"><CheckCircle2 size={13} className="text-[#7CFC7C]" /> Tick off tasks</span>
            <span className="inline-flex items-center gap-1 text-[#E6A519] osrs-shadow text-[11px]"><Flame size={13} className="text-[#FF9040]" /> Build a streak</span>
            <span className="inline-flex items-center gap-1 text-[#E6A519] osrs-shadow text-[11px]"><Ticket size={13} className="text-[#FFCF3F]" /> Monthly #1 wins a free Bond</span>
          </div>
          <button data-testid="demo-play-guest" onClick={() => navigate(`/guest/${encodeURIComponent(result.rsn)}?gm=${gm}`)}
            className="osrs-btn osrs-btn-green w-full py-2.5 mt-3 flex items-center justify-center gap-1">
            <Play size={15} /> Play these tasks now — no signup
          </button>
          <button data-testid="demo-register-cta" onClick={() => onRegister(result.rsn, gm)} className="osrs-btn w-full py-2 mt-2">
            Claim my board &amp; save progress →
          </button>
          <a data-testid="demo-share" href={`${API}/try/${encodeURIComponent(result.rsn)}`} target="_blank" rel="noreferrer"
            className="osrs-btn w-full py-2 mt-2 flex items-center justify-center gap-1">
            <Share2 size={14} /> Share my board
          </a>
        </div>
      )}
    </div>
  );
};
